/**
 * Composed-assessment report watermark.
 *
 * The classification is ALWAYS read from the server (admin_get_assessment_safety).
 * If it cannot be read, we fail closed and watermark the document.
 *
 * Legacy (uncomposed) assessments are exempt: their output stays byte-identical.
 */
import type jsPDF from "jspdf";
import { supabase } from "@/integrations/supabase/client";
import { DRAFT_WATERMARK_LINES, DRAFT_WATERMARK_TEXT } from "@/components/report/ReportClassificationBanner";

export type WatermarkDecision = {
  required: boolean;
  classification: string;
  composed: boolean;
};

export async function resolveReportWatermark(
  assessmentId: string,
): Promise<WatermarkDecision> {
  // IMPORTANT: use the client-safe, authenticated-callable classification RPC.
  // The admin-only admin_get_assessment_safety RPC raises "Administrators only"
  // for reviewers/respondents, which previously fell into the fail-closed branch
  // and watermarked LEGACY reports. The server remains the sole source of truth.
  try {
    const { data, error } = await (supabase.rpc as any)(
      "report_readiness_classification",
      { p_assessment_id: assessmentId },
    );
    if (error) throw error;
    const row = (Array.isArray(data) ? data[0] : data) as any;
    if (!row) throw new Error("empty classification");

    const composed = Boolean(row.composed);
    if (!composed) {
      // Legacy / uncomposed assessment: previous behaviour, never watermarked.
      return { required: false, classification: "legacy", composed: false };
    }
    return {
      required: Boolean(row.watermark_required),
      classification: String(row.classification ?? "draft_unbound"),
      composed: true,
    };
  } catch (err) {
    console.warn("[report-watermark] classification lookup failed", err);
    // Fail closed only for assessments we can positively confirm are composed.
    // A legacy assessment must never be watermarked because of a lookup error.
    try {
      const { data: composed, error: cErr } = await (supabase.rpc as any)(
        "assessment_is_composed",
        { p_assessment_id: assessmentId },
      );
      if (cErr) throw cErr;
      if (composed === false) {
        return { required: false, classification: "legacy", composed: false };
      }
    } catch (probeErr) {
      console.warn("[report-watermark] composed probe failed; failing closed", probeErr);
    }
    return { required: true, classification: "draft_unbound", composed: true };
  }
}


/**
 * Stamps every page with a diagonal watermark and writes the classification
 * into the PDF metadata. Must be called immediately before save/output.
 */
export function applyReportWatermark(doc: jsPDF, decision: WatermarkDecision) {
  if (!decision.required) return;

  const pageCount = doc.getNumberOfPages();
  for (let page = 1; page <= pageCount; page++) {
    doc.setPage(page);
    const w = doc.internal.pageSize.getWidth();
    const h = doc.internal.pageSize.getHeight();

    doc.saveGraphicsState();
    // @ts-expect-error GState is attached at runtime by jsPDF
    doc.setGState(new doc.GState({ opacity: 0.14 }));
    doc.setTextColor(200, 30, 30);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(Math.min(w, h) * 0.075);
    DRAFT_WATERMARK_LINES.forEach((line, i) => {
      doc.text(line, w / 2, h / 2 + (i - 1) * (h * 0.09), {
        align: "center",
        angle: 35,
      });
    });
    doc.restoreGraphicsState();
  }

  doc.setProperties({
    title: `${DRAFT_WATERMARK_TEXT}`,
    subject: `Report classification: ${decision.classification}`,
    keywords: DRAFT_WATERMARK_LINES.join(","),
  });
}

/** Prefix applied to the download filename so the state survives outside the file. */
export function watermarkFilename(filename: string, decision: WatermarkDecision) {
  return decision.required ? `DRAFT_UNBOUND_${filename}` : filename;
}
